import type { AssistantStateResponse, RivetsClient, StartAssistantConversationResponse } from "@rivets/core";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { assistantContextIsCurrent, landedChatId, useAssistantDrawer } from "./assistantStore";
import { stopChat } from "@/features/chat/optimisticStop";

interface AssistantActionsOptions {
  orgId: string;
  context: string;
  generation: number;
  queryKey: QueryKey;
  queryClient: QueryClient;
  client: Pick<RivetsClient, "assistantState" | "startAssistantConversation" | "sendMessage" | "archiveAssistantConversation" | "resetAssistant" | "cancelChat">;
}

/** Update the cache before selecting a new chat, including a replacement session. */
export function adoptAssistantConversation(previous: AssistantStateResponse | undefined, created: StartAssistantConversationResponse): AssistantStateResponse {
  const existing = previous?.session?.id === created.session.id ? previous.conversations : [];
  return {
    session: created.session, available: true,
    conversations: [created.conversation, ...existing.filter((item) => item.chatId !== created.chat.id)],
  };
}

/** Effects are independent of React so context changes and delayed requests can
 * be exercised with real query-cache behavior. Every completion is fenced by
 * the context generation, including A → B → A navigation. */
export function createAssistantActions(options: AssistantActionsOptions) {
  const { orgId, context, generation, queryKey, queryClient, client } = options;
  const current = () => assistantContextIsCurrent(context, generation);
  const begin = (busy: NonNullable<ReturnType<typeof useAssistantDrawer.getState>["busy"]>) => {
    if (!current() || useAssistantDrawer.getState().busy) return false;
    useAssistantDrawer.setState({ busy, error: undefined });
    return true;
  };
  const finish = () => { if (current()) useAssistantDrawer.setState({ busy: undefined }); };
  const fail = (error: unknown) => {
    if (current()) useAssistantDrawer.setState({ error: error instanceof Error ? error.message : "The request failed. Please try again." });
  };
  const replaceState = async (state: AssistantStateResponse) => {
    await queryClient.cancelQueries({ queryKey, exact: true });
    if (!current()) return false;
    queryClient.setQueryData(queryKey, state);
    return true;
  };

  return {
    async send(text: string, chatId?: string): Promise<void> {
      const trimmed = text.trim();
      if (!trimmed || !begin(chatId ? "send" : "start")) return;
      const id = crypto.randomUUID();
      useAssistantDrawer.getState().addPending({
        id, chatId: chatId ?? "", text: trimmed, createdAt: Date.now(),
      });
      try {
        if (chatId) {
          const response = await client.sendMessage(chatId, { text: trimmed, clientRequestId: id });
          if (!current()) return;
          const landed = landedChatId(response, chatId);
          if (landed) {
            useAssistantDrawer.getState().adoptPending(id, landed);
            let state: AssistantStateResponse;
            try { state = await client.assistantState(orgId); }
            catch {
              if (current()) useAssistantDrawer.setState({ error: "Your message was sent to a new workspace, but its conversation could not be loaded. Refresh the assistant to reconnect." });
              return;
            }
            if (!current() || !await replaceState(state)) return;
            // A deliberate navigation while sending should remain in place.
            const view = useAssistantDrawer.getState().view;
            if (view.kind === "conversation" && view.chatId === chatId) useAssistantDrawer.getState().openConversation(landed);
          } else {
            void queryClient.invalidateQueries({ queryKey, exact: true });
          }
          return;
        }
        const created = await client.startAssistantConversation(orgId, { prompt: trimmed, clientRequestId: id });
        if (!current()) return;
        await queryClient.cancelQueries({ queryKey, exact: true });
        if (!current()) return;
        queryClient.setQueryData<AssistantStateResponse>(queryKey, (old) => adoptAssistantConversation(old, created));
        queryClient.setQueryData(["chat", created.chat.id], { chat: created.chat, jobs: [created.job] });
        useAssistantDrawer.getState().adoptPending(id, created.chat.id);
        useAssistantDrawer.getState().openConversation(created.chat.id);
      } catch (error) {
        if (!current()) return;
        useAssistantDrawer.getState().failPending(id, error instanceof Error ? error.message : "Could not send that message.");
      } finally { finish(); }
    },
    async archive(chatId: string): Promise<void> {
      if (!begin("archive")) return;
      try {
        await client.archiveAssistantConversation(orgId, chatId);
        if (!current()) return;
        await queryClient.cancelQueries({ queryKey, exact: true });
        if (!current()) return;
        queryClient.setQueryData<AssistantStateResponse>(queryKey, (old) => old ? {
          ...old, conversations: old.conversations.filter((item) => item.chatId !== chatId),
        } : old);
        const drawer = useAssistantDrawer.getState();
        if (drawer.view.kind === "conversation" && drawer.view.chatId === chatId) drawer.showList();
        useAssistantDrawer.setState({ pending: drawer.pending.filter((item) => item.chatId !== chatId) });
      } catch (error) { fail(error); }
      finally { finish(); }
    },
    async reset(): Promise<void> {
      if (!begin("reset")) return;
      try {
        const state = await client.resetAssistant(orgId);
        if (!current() || !await replaceState(state)) return;
        useAssistantDrawer.setState({ view: { kind: "list" }, pending: [], unread: false });
      } catch (error) { fail(error); }
      finally { finish(); }
    },
    async cancel(chatId: string): Promise<void> {
      if (!begin("cancel")) return;
      try {
        // The run is named once, so this Stop cannot land on a later one;
        // with none in hand the server stops whatever is active.
        const worktreeId = queryClient.getQueryData<AssistantStateResponse>(queryKey)?.session?.worktreeId ?? "";
        await stopChat(queryClient, { id: chatId, worktreeId }, client);
        if (!current()) return;
        void queryClient.invalidateQueries({ queryKey: ["chat", chatId], exact: true });
      } catch (error) { fail(error); }
      finally { finish(); }
    },
  };
}
